/**
 * SiblingPagesSidebar — list of every managed page in the current topic
 * with a click-to-navigate item per row. Collapses on desktop to just a
 * chevron, slides over content on mobile.
 *
 * The sidebar is a presentation layer — it doesn't decide where each
 * sibling navigates to. Pass `buildHref(siblingId)` so the host page can
 * keep users on its own surface while sharing the chrome.
 */
import { Link } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { useEscapeKey } from '../hooks/useEscapeKey';
import api from '../services/api';
import { useLiveReload } from './common/useLiveReload';
import { useWebSocket } from '../contexts/WebSocketContext';
import { SitePlanChip } from './common/SitePlanChip';
import { countWords, formatWordCount } from './contentSize';
import type { ManagedPage, PageBuildStatus, WebSocketEvent } from '../types';

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  PENDING: 'Pending',
  QUEUED: 'Queued',
  IN_PROGRESS: 'In Progress',
  WAITING_FOR_USER_ACTION: 'Waiting for User',
  WAITING_FOR_SYSTEM_ACTION: 'Waiting for System',
  READY_TO_DEPLOY: 'Ready to Deploy',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  CANCELLED: 'Canceled',
  SKIPPED: 'Skipped',
  ERROR: 'Error',
};

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
  PENDING: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  QUEUED: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
  IN_PROGRESS: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300',
  WAITING_FOR_USER_ACTION: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
  WAITING_FOR_SYSTEM_ACTION: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
  READY_TO_DEPLOY: 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300',
  COMPLETED: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
  FAILED: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  CANCELLED: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
  SKIPPED: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
  ERROR: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
};

/** Whether the desktop sidebar is expanded. A preference about the person, not the
 *  page: opening it once should hold as you click from sibling to sibling, and on your
 *  next visit. */
const OPEN_KEY = 'jcseo_sibling_pages_sidebar';

function readOpen(): boolean {
  try {
    return localStorage.getItem(OPEN_KEY) === 'open';
  } catch {
    return false; // private mode / storage disabled — not a reason to break the sidebar
  }
}

function writeOpen(open: boolean): void {
  try {
    localStorage.setItem(OPEN_KEY, open ? 'open' : 'shut');
  } catch {
    /* preference is a nicety; never let it throw into the render path */
  }
}

function statusLabel(status: PageBuildStatus | null): string {
  if (!status) return 'No Status';
  return STATUS_LABELS[status] || status;
}

function statusColor(status: PageBuildStatus | null): string {
  if (!status) return 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500';
  return STATUS_COLORS[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
}

export interface SiblingPagesSidebarProps {
  currentPageId: number;
  topicId: number | null | undefined;
  buildHref: (siblingId: number) => string;
}

export default function SiblingPagesSidebar({
  currentPageId,
  topicId,
  buildHref,
}: SiblingPagesSidebarProps) {
  const { subscribe } = useWebSocket();
  const [siblings, setSiblings] = useState<ManagedPage[]>([]);
  const [loading, setLoading] = useState(false);
  // Starts collapsed so the page builder gets the full content width by default; the
  // vertical strip below makes it obvious how to expand. Once expanded it stays that
  // way — remounting on every sibling click is not a reason to shut it again.
  const [open, setOpenState] = useState(readOpen);
  const setOpen = useCallback((next: boolean) => {
    writeOpen(next);
    setOpenState(next);
  }, []);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Let <Esc> close the mobile slide-over drawer.
  useEscapeKey(mobileOpen, () => setMobileOpen(false));

  const fetchSiblings = useCallback(async () => {
    if (!topicId) {
      setSiblings([]);
      return;
    }
    setLoading(true);
    try {
      const data = await api.getManagedPages({
        topicId,
        size: 100,
        sortBy: 'keyword',
        sortOrder: 'asc',
      });
      setSiblings(data.items);
    } catch (err) {
      console.error('Failed to load sibling pages:', err);
    } finally {
      setLoading(false);
    }
  }, [topicId]);

  useEffect(() => {
    fetchSiblings();
  }, [fetchSiblings]);

  // Live status badges: a `page_build` event for a page already in this
  // list means its DB status changed — refetch so the badge re-renders.
  // `event.status` is a websocket lifecycle value, not the DB enum, so we
  // refetch rather than patch the row in place.
  useEffect(() => {
    return subscribe((event: WebSocketEvent) => {
      if (event.event_type !== 'page_build') return;
      if (siblings.some((p) => p.id === event.page_id)) {
        fetchSiblings();
      }
    });
  }, [subscribe, siblings, fetchSiblings]);
  // A NEW PAGE in the topic has an id the check above never heard of.
  useLiveReload(
    (e) =>
      (e?.event_type === 'page_build' && e.status === 'started' && !siblings.some((p) => p.id === e.page_id)) ||
      (e?.event_type === 'growth_agent_llm' && e.status === 'session_complete'),
    () => void fetchSiblings(),
    3_000,
  );

  if (!topicId) return null;

  const list = (onNavigate?: () => void) =>
    loading ? (
      <div className="p-3 text-sm text-gray-500 dark:text-gray-400">Loading...</div>
    ) : siblings.length === 0 ? (
      <div className="p-3 text-sm text-gray-400 dark:text-gray-500">No sibling pages</div>
    ) : (
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {siblings.map((sibling) => {
          const isActive = sibling.id === currentPageId;
          // How much article is there. The status badge says where the page is in
          // the pipeline; this says whether anything was actually written — the
          // two disagree often enough to be worth showing both.
          const words = countWords(sibling.content);
          return (
            <Link
              key={sibling.id}
              to={buildHref(sibling.id)}
              onClick={() => onNavigate?.()}
              className={`block px-3 py-2.5 text-sm transition-colors ${
                isActive
                  ? 'bg-purple-50 dark:bg-purple-900/30 border-l-3 border-l-purple-500'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 border-l-3 border-l-transparent'
              }`}
            >
              <div className="flex items-baseline gap-2">
                <div
                  className={`truncate flex-1 min-w-0 ${
                    isActive ? 'font-medium text-purple-700 dark:text-purple-300' : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {sibling.keyword_value || 'Untitled'}
                </div>
                <span
                  className={`flex-shrink-0 text-[10px] tabular-nums ${
                    words > 0
                      ? 'text-gray-500 dark:text-gray-400'
                      : 'italic text-gray-400 dark:text-gray-600'
                  }`}
                  title={words > 0 ? `${words.toLocaleString()} words` : 'Nothing written yet'}
                >
                  {formatWordCount(words)}
                </span>
              </div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span
                  className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${statusColor(
                    sibling.status,
                  )}`}
                >
                  {statusLabel(sibling.status)}
                </span>
                {sibling.managed_site_name && (
                  <SitePlanChip
                    siteName={sibling.managed_site_name}
                    planName={sibling.deployment_plan_name}
                    status={sibling.status}
                  />
                )}
              </div>
            </Link>
          );
        })}
      </div>
    );

  return (
    <>
      {/* Mobile toggle — fixed, floats over content. */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-20 left-2 z-30 p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
        title="Show sibling pages"
        type="button"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
      </button>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 w-[280px] bg-gray-50 dark:bg-gray-800 shadow-xl flex flex-col">
            <div className="px-3 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Pages ({loading ? '...' : siblings.length})
              </span>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded"
                type="button"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {list(() => setMobileOpen(false))}
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar — sticky to viewport top, collapses to a chevron. */}
      {open ? (
        <div className="hidden lg:flex w-[280px] flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 sticky top-0 self-start max-h-screen overflow-hidden flex-col">
          <div className="px-3 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Pages ({loading ? '...' : siblings.length})
            </span>
            <button
              onClick={() => setOpen(false)}
              className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded"
              title="Collapse sidebar"
              type="button"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">{list()}</div>
        </div>
      ) : (
        // Collapsed strip — full-height vertical "Pages (N)" tab so it's
        // visually obvious there's a sidebar that can be opened, instead
        // of a lonely chevron that needs a tooltip.
        <div className="hidden lg:flex flex-shrink-0 sticky top-0 self-start max-h-screen">
          <button
            onClick={() => setOpen(true)}
            className="group flex flex-col items-center gap-2 py-3 px-1.5 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 min-h-[160px] w-8"
            title="Show sibling pages"
            type="button"
          >
            <svg
              className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
            <span
              className="text-xs font-medium tracking-wide whitespace-nowrap [writing-mode:vertical-rl] rotate-180"
            >
              Pages ({loading ? '…' : siblings.length})
            </span>
          </button>
        </div>
      )}
    </>
  );
}
